/**
 * 공통 user-env Map 조작 유틸
 * 저장소는 건드리지 않고, Map 또는 배열을 인자로 받아 결과만 반환한다.
 */

/** API 응답 등에서 쓰이는 최소 user env 항목 (key/value만 사용) */
export type UserEnvEntry = { key?: string; value?: string };

/**
 * 문자열을 JSON으로 파싱 시도. 실패 시 원본 문자열 반환.
 * @param val - 저장된 값
 * @returns 파싱된 값 또는 원본 문자열
 */
export function parseUserEnvValue(val?: string): unknown {
  if (!val) {
    return val;
  }
  try {
    return JSON.parse(val);
  } catch {
    return val;
  }
}

/**
 * UserEnvEntry 배열을 Map<string, string>으로 변환 (key/value 있는 항목만)
 * @param data - API 등에서 받은 배열
 * @returns 새 Map (빈 배열/undefined면 빈 Map)
 */
export function userEnvDataToMap(data?: UserEnvEntry[]): Map<string, string> {
  if (!Array.isArray(data)) {
    return new Map();
  }

  return new Map(data.filter((item) => !!item.key).map((item) => [item.key!, item.value ?? '']));
}

/**
 * 기존 Map에 data 항목을 병합한 새 Map 반환
 * @param map - 기존 Map
 * @param data - 추가할 항목 배열
 * @returns 새 Map
 */
export function mergeUserEnvDataIntoMap(
  map: Map<string, string>,
  data?: UserEnvEntry[],
): Map<string, string> {
  const next = new Map(map);
  data?.forEach((item) => {
    if (item.key) {
      next.set(item.key, item.value ?? '');
    }
  });
  return next;
}

/**
 * Map에서 키 하나의 값을 파싱해 반환
 * @param map - env Map
 * @param key - 키
 * @returns 파싱된 값 또는 null
 */
export function getEnvValueFromMap<T>(map: Map<string, string>, key?: string): T | null {
  if (!key) return null;
  const raw = map.get(key);
  const parsed = parseUserEnvValue(raw);
  return (parsed ?? null) as T | null;
}

/**
 * Map에서 여러 키에 대한 값을 배열로 반환 [{ key1: v1 }, { key2: v2 }, ...]
 * @param map - env Map
 * @param keys - 키 배열
 * @returns 각 키별 파싱된 값 객체 배열
 */
export function getEnvValuesFromMap(
  map: Map<string, string>,
  keys: string[],
): { [key in string]: unknown | null }[] {
  return keys.map((key) => ({ [key]: parseUserEnvValue(map.get(key)) ?? null }));
}

/**
 * Map에서 키에 word가 포함된 항목만 모아 객체로 반환
 * @param map - env Map
 * @param word - 키에 포함될 문자열
 * @returns 키-파싱값 객체
 */
export function getEnvValueByPatternFromMap(
  map: Map<string, string>,
  word: string,
): { [key in string]: unknown | null } {
  const result: { [key in string]: unknown | null } = {};

  if (word.trim() === '') {
    return result;
  }

  map.forEach((value, key) => {
    if (key.includes(word)) {
      result[key] = parseUserEnvValue(value) ?? null;
    }
  });
  return result;
}
